import React from 'react';
import classnames from 'classnames';
import { useIntl } from 'umi';
import SensorTowerTagsSelect from '@intelligence/components/SensorTowerTagsSelect';
import Select from '@intelligence/v3/components/FormComponents/Select';
import { getOptionLocaleText, getPreviewTitle } from '../previewHelpers';
import { getStringArrayValue } from '../shared/valueUtils';
import { COMMON_JOINER, analysisDataSourceMetricKey } from '@intelligence/utils/topchartsUtils';
import { FILTER_TYPE } from '../filterTypes';

import type { IFilterRenderProps, IFilterTypeEntry } from '@intelligence/v3/filterCore/registry';
import type { IFilterControlModule } from '../controlModule';
import type { ITagFilterValue } from './types';

import ICON_APPMAGIC from '@intelligence/assets/imgs/dataSource/app-magic.png';
import ICON_SENSORTOWER from '@intelligence/assets/svg/dataSource/sensor-tower.svg';

const getTagIcon = (field: string, icon?: string): string | undefined => {
  if (icon) {
    return icon;
  }

  if (field.startsWith('appmagic_')) {
    return ICON_APPMAGIC;
  }

  if (field.startsWith('sensor_tower_')) {
    return ICON_SENSORTOWER;
  }

  return undefined;
};

const getThemeLeafOptionValues = (options: any[]): string[] => {
  if (!Array.isArray(options)) {
    return [];
  }

  return options.reduce<string[]>((result, option) => {
    const children = Array.isArray(option?.children) ? option.children : [];

    children.forEach((child: { value?: string }) => {
      if (typeof child?.value === 'string' && child.value) {
        result.push(child.value);
      }
    });

    return result;
  }, []);
};

const getTagOptionDisplayText = (
  option: any,
  locale: string,
  formatMessage: ({ id }: { id: string }) => string,
): string => {
  const textKey = option?.text;

  if (typeof textKey === 'string' && textKey.startsWith('dbi_')) {
    return formatMessage({ id: textKey });
  }

  return getOptionLocaleText(option, locale);
};

const TagSelectRenderer: React.FC<IFilterRenderProps<ITagFilterValue>> = (props) => {
  const {
    field,
    label = '',
    title,
    icon,
    value,
    onChange,
    singleSelect,
    options = [],
    locale: _locale,
    item: _item,
    className,
    ...rest
  } = props;

  if (singleSelect) {
    return (
      <Select
        key={field}
        className={classnames('select-item', className)}
        label={title || label}
        options={options}
        value={getStringArrayValue(value)[0] || ''}
        searchable
        appearance="button"
        onChange={(nextValue: string) => {
          onChange(nextValue ? [nextValue] : []);
        }}
      />
    );
  }

  return (
    // @ts-expect-error SensorTowerTagsSelect 缺少完整 props 类型定义
    <SensorTowerTagsSelect
      {...rest}
      key={field}
      filterKey={field}
      title={title || label}
      icon={getTagIcon(field, icon)}
      value={getStringArrayValue(value)}
      onChange={(payload: string[]) => {
        onChange(payload);
      }}
    />
  );
};

const TagPreview: React.FC<{
  item: Parameters<NonNullable<IFilterTypeEntry['renderPreview']>>[0];
  valueItem: any;
}> = (props) => {
  const { item, valueItem } = props;
  const { locale, formatMessage } = useIntl();
  const value = Array.isArray(valueItem) ? valueItem : valueItem?.value || [];
  const options = item.controlProps?.options || [];
  const title = item.controlProps?.title;
  const isAppMagic = item.field.startsWith('appmagic_');
  const isTheme = item.type === FILTER_TYPE.SENSOR_TOWER_THEME;

  let content: React.ReactNode = formatMessage({
    id: 'dbi_filterSave_none',
  });

  if (value.length === 1) {
    if (isTheme) {
      const theme = analysisDataSourceMetricKey(value[0], COMMON_JOINER);
      const themeTag = options.find((option: any) => option?.value === theme?.dataSource);
      const target = themeTag?.children?.find((child: any) => child?.value === value[0]);

      content = getOptionLocaleText(target, locale) || theme?.metric || value[0];
    } else {
      const target = options.find((option: any) => option?.value === value[0]);
      content = getTagOptionDisplayText(target, locale, formatMessage) || value[0];
    }
  } else if (value.length > 1) {
    const themeLeafOptionValues = isTheme ? getThemeLeafOptionValues(options) : [];
    const allMatch =
      isTheme
        ? themeLeafOptionValues.length > 0 &&
          value.length === themeLeafOptionValues.length &&
          value.every((itemValue: string) => themeLeafOptionValues.includes(itemValue))
        : Array.isArray(options) &&
          options.length > 0 &&
          value.length === options.length &&
          value.every((itemValue: string) => options.some((opt: any) => opt?.value === itemValue));
    content = allMatch ? formatMessage({ id: 'dbi_all' }) : value.length;
  }

  return (
    <>
      <span className="model-form-text">
        {isAppMagic && <img className="model-form-text__icon" src={ICON_APPMAGIC} alt="" />}
        {typeof title === 'string' && title.startsWith('dbi_')
          ? formatMessage({ id: title })
          : getPreviewTitle(item, formatMessage)}
      </span>
      <span className="model-form-context">{content}</span>
    </>
  );
};

const getRenderPreview = (): NonNullable<IFilterTypeEntry['renderPreview']> => {
  // eslint-disable-next-line react/display-name
  return (item, value) => <TagPreview item={item} valueItem={value} />;
};

export const tagSelectControl: IFilterControlModule = {
  type: FILTER_TYPE.TAG_SELECT,
  entry: {
    render: (props) => {
      return <TagSelectRenderer {...props} />;
    },
  },
  getRenderPreview,
};
